/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Settings, BarChart3, TrendingUp } from "lucide-react";
import { WhatsAppAutoMessageSenderNavigations } from "../../components/WhatsAppAutoMessageSender/Navigations";
import { WhatsAppAutoMessageSenderDashboard } from "../../components/WhatsAppAutoMessageSender/Dashboard/Dashboard";
import { WhatsAppAutoMessageSenderForm } from "../../components/WhatsAppAutoMessageSender/Form";
import {
  fetchWhatsappTemplateById,
  fetchWhatsappTemplates,
} from "../../store/slices/whatsappDataSlice";
import { RootDispatch, RootState } from "../../store";
import { useDispatch, useSelector } from "react-redux";
import {
  addReportOfEachNumber,
  deleteAllObjectsFromS3Bucket,
  getCronJobStatus,
  getReportBasedOnDate,
  getReportOfAllNumbersBasedOnDate,
  getRoundRobinData,
  getWhatsAppAutomationReport,
  pauseCronJob,
  resumeCronJob,
  setIsPaused,
  startCronJobBasedOnRoundRobinData,
  stopCronJob,
  updateReportSummary,
  updateRoundRobinData,
  updateTodayReportSummary,
} from "../../store/slices/roundRobinSlice";
import { ConfigInfo } from "../../models/roundRobin.model";
import { generateWhatsAppTemplatePayloadAndUploadToS3 } from "../../utils/whatsappUtils/ManageTemplate/generateWhatsAppTemplatePayloadAndUploadToS3";
import { validateTemplateBeforeSending } from "../../utils/whatsappUtils/ManageTemplate/validateTemplateBeforeSending";
import { DocumentFileMap } from "../../models/whatsappTemplate.model";
import toast from "react-hot-toast";
import { fetchLeadCount } from "../../store/slices/leadSlice";
import socket from "../../utils/socketConnect.utils";
import CampaignsPage from "./CampaignsPage";

const nodeEnv = process.env.REACT_APP_NODE_ENV || "development";

const bucketName =
  nodeEnv === "production"
    ? process.env.REACT_APP_CRON_JOB_BUCKET_NAME_Prod
    : process.env.REACT_APP_CRON_JOB_BUCKET_NAME_Dev;

const validTabs = ["Campaignes", "Interal Auto Sender", "dashboard"] as const;

type TabName = (typeof validTabs)[number];

const getInitialTab = (): TabName => {
  if (typeof window === "undefined") return "Campaignes";

  const tabFromUrl = new URLSearchParams(window.location.search).get("tab");

  return validTabs.includes(tabFromUrl as TabName)
    ? (tabFromUrl as TabName)
    : "Campaignes";
};

const WhatsAppAutoMessageSenderPage: React.FC = () => {
  const dispatch: RootDispatch = useDispatch();

  const body = useSelector(
    (state: RootState) => state.roundRobin.configInfo.body,
  );
  const footer = useSelector(
    (state: RootState) => state.roundRobin.configInfo.footer,
  );
  const headers = useSelector(
    (state: RootState) => state.roundRobin.configInfo.headers,
  );

  const isCronJobRunning = useSelector(
    (state: RootState) => state.roundRobin.cronJobStatus,
  );
  const configInfo = useSelector(
    (state: RootState) => state.roundRobin.configInfo,
  );

  async function getTemplatesById(id: any) {
    await dispatch(fetchWhatsappTemplateById(id));
  }
  const whatsappTemplates = useSelector(
    (state: RootState) => state.whatsAppData.templates,
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [documentFile, setDocumentFile] = useState<DocumentFileMap>({});
  const [componentInputs, setComponentInputs] = useState<any>({});
  const getCronStatus = async (): Promise<void> => {
    await dispatch(getCronJobStatus());
    return;
  };

  const [activeTab, setActiveTab] = useState<TabName>(getInitialTab);

  const handleTabChange = (tab: string) => {
    const safeTab = validTabs.includes(tab as TabName)
      ? (tab as TabName)
      : "Campaignes";

    setActiveTab(safeTab);

    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      params.set("tab", safeTab);

      const queryString = params.toString();
      const nextUrl = queryString
        ? `${window.location.pathname}?${queryString}`
        : window.location.pathname;

      window.history.replaceState({}, "", nextUrl);
    }
  };

  const selectedDate = useSelector(
    (state: RootState) => state.roundRobin.selectedDate,
  );
  const [isRefreshing, setIsRefreshing] = useState(false);

  const singleTemplate = useSelector(
    (state: RootState) => state.whatsAppData.singleTemplate,
  );

  const selectDateForPerformance = useSelector(
    (state: RootState) => state.roundRobin.selectDateForPerformance,
  );
  const isPaused = useSelector((state: RootState) => state.roundRobin.isPaused);

  const [config, setConfig] = useState<ConfigInfo>({
    cap: 0,
    numberOfTimesToSendMsg: 0,
    numberOfDaysBeforeReset: 0,
    AlertMobileNumbers: [],
    template: "",
    messageSentCount: 0,
    noOfDaysCount: 0,
    delay: 0,
    isPaused: false,
    headers: headers,
    body: body,
    footer: footer,
    campaignTime: configInfo?.campaignTime,
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      dispatch(getWhatsAppAutomationReport());
      await dispatch(getReportBasedOnDate(selectDateForPerformance));
      await dispatch(getReportOfAllNumbersBasedOnDate(selectedDate));

      toast.success("Data refreshed successfully!");
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast.error("Failed to refresh data. Please try again.");
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (!socket.connected) socket.connect();

    socket.on("connect", () => {
      console.log("✅ Connected to socket:", socket.id);
    });

    socket.on("whatsappAutomationReportSummary", (data) => {
      console.log("📥 whatsappAutomationReportSummary received", data);
      dispatch(updateReportSummary(data));
    });

    socket.on("whatsappAutomationReportSummaryForToday", (data) => {
      if (isToday(selectDateForPerformance)) {
        dispatch(updateTodayReportSummary(data));
      } else {
        console.log(
          "📅 Ignored today's summary because selected date is not today.",
        );
      }
    });

    socket.on("whatsappAutomationReportDetailsOfEachNumber", (data) => {
      dispatch(addReportOfEachNumber(data));
    });

    socket.on("disconnect", () => {
      console.log("❌ Socket disconnected");
    });

    return () => {
      socket.off("connect");
      socket.off("disconnect");
      socket.off("whatsappAutomationReportSummary");
      socket.off("whatsappAutomationReportSummaryForToday");
      socket.off("whatsappAutomationReportDetailsOfEachNumber");
    };
  }, [dispatch, selectDateForPerformance]);

  function isToday(dateStr: string): boolean {
    const today = new Date();
    const inputDate = new Date(dateStr);
    return (
      inputDate.getDate() === today.getDate() &&
      inputDate.getMonth() === today.getMonth() &&
      inputDate.getFullYear() === today.getFullYear()
    );
  }

  useEffect(() => {
    dispatch(getReportBasedOnDate(selectDateForPerformance));
  }, [dispatch, selectDateForPerformance, selectedDate]);

  useEffect(() => {
    dispatch(isRoundRobinDataAvailable);
  }, [
    dispatch,
    configInfo.cap,
    configInfo.numberOfTimesToSendMsg,
    configInfo.numberOfDaysBeforeReset,
    configInfo.template,
    configInfo.messageSentCount,
    configInfo.noOfDaysCount,
    configInfo.AlertMobileNumbers,
  ]);

  useEffect(() => {
    dispatch(getReportOfAllNumbersBasedOnDate(selectedDate));
  }, [dispatch, selectedDate]);

  const handleStartCronJob = async () => {
    if (
      !validateTemplateBeforeSending(
        singleTemplate,
        documentFile ?? {},
        componentInputs,
      )
    ) {
      return;
    }

    try {
      const { components: builtComponents } =
        await generateWhatsAppTemplatePayloadAndUploadToS3(
          singleTemplate,
          documentFile ?? {},
          componentInputs,
          configInfo,
          dispatch,
        );

      const updatedConfig = {
        ...configInfo,
        components: builtComponents,
        campaignTime: config.campaignTime,
      };
      setConfig(updatedConfig);

      await dispatch(startCronJobBasedOnRoundRobinData());
      await dispatch(getRoundRobinData());
    } catch (error) {
      console.error("Error starting cron job:", error);
    }
  };

  const handleStopCronJob = async () => {
    try {
      await dispatch(deleteAllObjectsFromS3Bucket(bucketName!));
      await dispatch(stopCronJob());
      await dispatch(getCronJobStatus());
    } catch (error) {
      console.error("Error starting cron job:", error);
    }
  };

  const pauseCronJobHandler = async () => {
    try {
      await dispatch(pauseCronJob());
      dispatch(setIsPaused(true));
      await dispatch(getRoundRobinData());
      await dispatch(getCronJobStatus());
    } catch (error) {
      console.error("Error pausing cron job:", error);
    }
  };

  const resumeCronJobHandler = async () => {
    if (
      !validateTemplateBeforeSending(
        singleTemplate,
        documentFile ?? {},
        componentInputs,
      )
    ) {
      return;
    }

    try {
      const { components: builtComponents } =
        await generateWhatsAppTemplatePayloadAndUploadToS3(
          singleTemplate,
          documentFile ?? {},
          componentInputs,
          config,
          dispatch,
        );

      const updatedConfig = {
        ...config,
        components: builtComponents,
        campaignTime: config.campaignTime,
      };
      setConfig(updatedConfig);
      await dispatch(resumeCronJob());
      dispatch(setIsPaused(false));
      await dispatch(getRoundRobinData());
      await dispatch(getCronJobStatus());
    } catch (error) {
      console.error("Error resuming cron job:", error);
    }
  };

  useEffect(() => {
    dispatch(fetchLeadCount());
  }, [dispatch]);

  useEffect(() => {
    async function getTemplates() {
      await dispatch(fetchWhatsappTemplates());
    }
    getTemplates();
  }, [dispatch]);

  useEffect(() => {
    dispatch(getRoundRobinData());
  }, [dispatch]);

  const updateFormData = async () => {
    const isFormFilled =
      config.cap > 0 &&
      config.numberOfTimesToSendMsg > 0 &&
      config.numberOfDaysBeforeReset > 0 &&
      config.delay > 0 &&
      !!config.template;

    if (!isFormFilled) {
      toast.error(
        "Form is incomplete. Fill all required fields before saving.",
      );
      return;
    }

    console.log("incoming config for update:", config);
    await dispatch(updateRoundRobinData(config));
    await dispatch(getRoundRobinData());
  };

  useEffect(() => {
    dispatch(getWhatsAppAutomationReport());
  }, [dispatch]);
  useEffect(() => {
    dispatch(getCronJobStatus());
  }, [dispatch, isCronJobRunning]);

  useEffect(() => {
    setIsPaused(configInfo?.isPaused);
    setConfig({
      cap: configInfo?.cap,
      numberOfTimesToSendMsg: configInfo?.numberOfTimesToSendMsg,
      numberOfDaysBeforeReset: configInfo?.numberOfDaysBeforeReset,
      AlertMobileNumbers: configInfo?.AlertMobileNumbers,
      messageSentCount: configInfo?.messageSentCount,
      template: configInfo?.template,
      noOfDaysCount: configInfo?.noOfDaysCount,
      delay: configInfo?.delay || 0,
      isPaused: configInfo?.isPaused || false,
      _id: configInfo?._id,
      documentUrl: configInfo?.documentUrl || "",
      documentFilename: configInfo?.documentFilename || "",
      headers: configInfo?.headers,
      body: configInfo?.body,
      footer: configInfo?.footer,
      campaignTime: configInfo.campaignTime,
    });
  }, [configInfo, body, footer, headers]);

  useEffect(() => {
    if (whatsappTemplates && config.template && whatsappTemplates.length > 0) {
      const matchingTemplate = whatsappTemplates.find(
        (t: any) => t.name === config.template,
      );
      if (matchingTemplate) {
        setSelectedTemplateId(matchingTemplate.id);
        // Load the template details when template is found
        getTemplatesById(matchingTemplate.id);
      }
    }
  }, [config.template, whatsappTemplates]);

  const isRoundRobinDataAvailable = () => {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <WhatsAppAutoMessageSenderNavigations
          Settings={Settings}
          BarChart3={BarChart3}
          TrendingUp={TrendingUp}
          setActiveTab={handleTabChange}
          activeTab={activeTab}
        />

        {activeTab === "dashboard" && (
          <WhatsAppAutoMessageSenderDashboard
            handleRefresh={handleRefresh}
            isRefreshing={isRefreshing}
          />
        )}
        {activeTab === "Campaignes" && <CampaignsPage />}
        {activeTab === "Interal Auto Sender" && (
          <WhatsAppAutoMessageSenderForm
            getTemplatesById={getTemplatesById}
            selectedTemplateId={selectedTemplateId}
            setSelectedTemplateId={setSelectedTemplateId}
            config={config}
            updateFormData={updateFormData}
            setConfig={setConfig}
            stopCronJob={handleStopCronJob}
            documentFile={documentFile}
            setComponentInputs={setComponentInputs}
            componentInputs={componentInputs}
            setDocumentFile={setDocumentFile}
            handleStartCronJob={handleStartCronJob}
            getCronJobStatus={getCronStatus}
            isPaused={isPaused}
            setIsPaused={setIsPaused}
            handlePauseCronJob={pauseCronJobHandler}
            handleResumeCronJob={resumeCronJobHandler}
          />
        )}
      </div>
    </div>
  );
};

export default WhatsAppAutoMessageSenderPage;
